import React, { useCallback, useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_PACKAGES = [
  {
    name: 'YKS Koçluk Paketi',
    exam_type: 'YKS',
    price: '2.799',
    currency: 'TL',
    period: 'Aylık',
    description: 'YKS sınavına hazırlanan öğrenciler için özel koçluk programı',
    features: [
      'Sana Özel Derece Koçu veya PDR Uzmanı',
      'Sana Özel Günlük Program',
      'Haftada En Az 1 Canlı Görüşme',
      'Koçunla 7/24 iletişim desteği',
      'Ayda 1 Veli Görüşmesi',
      'Net Takibi ve Analiz',
      'Kaynak Önerileri',
    ],
    image_url: '',
    shopier_link: 'https://www.shopier.com/kargakademi/41304699',
    popular: true,
  },
  {
    name: 'KPSS Koçluk Paketi',
    exam_type: 'KPSS',
    price: '2.799',
    currency: 'TL',
    period: 'Aylık',
    description: 'KPSS sınavına hazırlanan adaylar için kapsamlı koçluk programı',
    features: [
      'Sana Özel Alanında Uzman PDR Koçu',
      'Sana Özel Günlük Program',
      'Haftada En Az 1 Canlı Görüşme',
      'Koçunla 7/24 iletişim desteği',
      'Net Takibi ve Analiz',
      'Kaynak Önerileri',
    ],
    image_url: '',
    shopier_link: 'https://www.shopier.com/kargakademi/41304699',
    popular: false,
  },
  {
    name: 'LGS Koçluk Paketi',
    exam_type: 'LGS',
    price: '2.799',
    currency: 'TL',
    period: 'Aylık',
    description: 'LGS sınavına hazırlanan öğrenciler için uzman koçluk desteği',
    features: [
      'Sana Özel Derece Koçu veya PDR Uzmanı',
      'Sana Özel Günlük Program',
      'Haftada En Az 1 Canlı Görüşme',
      'Koçunla 7/24 iletişim desteği',
      'Ayda 1 Veli Görüşmesi',
      'Net Takibi ve Analiz',
      'Kaynak Önerileri',
    ],
    image_url: '',
    shopier_link: 'https://www.shopier.com/kargakademi/41304699',
    popular: false,
  },
];

export default function PackagesPage() {
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [loading, setLoading] = useState(true);

  const loadPackages = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API}/packages`, {
        timeout: 12000,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      const nextPackages = Array.isArray(response.data) && response.data.length > 0 ? response.data : FALLBACK_PACKAGES;
      setPackages(nextPackages);
    } catch (error) {
      try {
        const retryResponse = await axios.get(`${API}/packages`, {
          timeout: 20000,
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });
        const retryPackages =
          Array.isArray(retryResponse.data) && retryResponse.data.length > 0
            ? retryResponse.data
            : FALLBACK_PACKAGES;
        setPackages(retryPackages);
      } catch (retryError) {
        setPackages(FALLBACK_PACKAGES);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="px-6 pb-24 pt-24">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-16 text-center">
            <span
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#B026FF]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Paketlerimiz
            </span>
            <h1
              className="mt-4 text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="packages-heading"
            >
              Sana Uygun Paketi Sec
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-[#A1A1AA] sm:text-lg">
              Hangi sinava hazirlaniyorsan, sana ozel koçluk paketimizle hedeflerine ulas.
            </p>
          </div>

          {loading && (
            <div className="mb-8 rounded-2xl border border-white/10 bg-[#0A0A0A] p-5 text-center text-[#A1A1AA]">
              Paketler yukleniyor...
            </div>
          )}

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg, index) => (
              <div
                key={`${pkg.exam_type}-${pkg.name}-${index}`}
                className={`overflow-hidden rounded-xl border bg-[#0A0A0A] transition-all hover:shadow-xl ${
                  pkg.popular
                    ? 'border-[#B026FF] shadow-lg shadow-[#B026FF]/10'
                    : 'border-white/10 hover:border-[#B026FF]/50'
                }`}
                data-testid={`package-card-${pkg.exam_type?.toLowerCase()}`}
              >
                {pkg.image_url && (
                  <div className="relative h-48 overflow-hidden">
                    <img src={pkg.image_url} alt={pkg.name} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 flex items-end justify-start bg-gradient-to-t from-[#0A0A0A] via-black/40 to-transparent p-4">
                      <span
                        className="text-3xl font-black tracking-tight text-white drop-shadow-lg"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {pkg.exam_type}
                      </span>
                    </div>
                  </div>
                )}

                {pkg.popular && (
                  <div className="flex items-center justify-center gap-1 bg-[#B026FF] py-1.5 text-center text-xs font-bold text-white">
                    <Star className="h-3 w-3" /> EN POPULER
                  </div>
                )}

                <div className="p-6">
                  <div className="mb-1">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#B026FF]">{pkg.exam_type}</span>
                  </div>
                  <h3 className="mb-2 text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {pkg.name}
                  </h3>
                  <p className="mb-4 text-sm text-[#A1A1AA]">{pkg.description}</p>
                  <div className="mb-6 flex items-end gap-1">
                    <span className="text-4xl font-black text-white">{pkg.price}</span>
                    <span className="mb-1 text-sm text-[#A1A1AA]">
                      {pkg.currency === 'TL' ? '₺' : pkg.currency} / {pkg.period}
                    </span>
                  </div>
                  <ul className="mb-6 space-y-3">
                    {(pkg.features || []).map((feature, featureIndex) => (
                      <li key={`${pkg.name}-feature-${featureIndex}`} className="flex items-start gap-2 text-sm text-[#E0E0E0]">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#B026FF]" /> {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => {
                      const link = pkg.shopier_link || 'https://www.shopier.com/kargakademi/41304699';
                      window.open(link, '_blank');
                    }}
                    className={`w-full text-white transition-all hover:-translate-y-1 ${
                      pkg.popular ? 'bg-[#B026FF] hover:bg-[#C966FF]' : 'bg-white/10 hover:bg-white/20'
                    }`}
                    data-testid={`buy-package-${pkg.exam_type?.toLowerCase()}`}
                  >
                    Satin Al
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
